/**
 * Wall-clock times that can fall on the day after the page's date: a festival
 * window that runs past midnight, or a tithi, nakshatra or yoga that ends at
 * 00:35 on the next calendar day. Kept in a leaf module so the client panchang
 * and tithi cards can share the festival pages' helper without pulling the
 * festival data module into their bundles.
 */

type Locale = "en" | "hi";

/** "HH:MM" from "YYYY-MM-DD HH:MM", plus a next-day marker when the day changes. */
export function clock(local: string, onDate: string, locale: Locale): string {
  const time = local.slice(11, 16);
  if (local.slice(0, 10) === onDate) return time;
  return locale === "hi" ? `${time} (अगले दिन)` : `${time} (next day)`;
}

/**
 * The engine's end of a panchang element as "YYYY-MM-DD HH:MM" for `clock`.
 * `end_time` is the rounded HH:MM shown everywhere; `ends_at` is the
 * offset-bearing instant ("2026-10-07T00:35:12+05:30") and supplies the date.
 * Rounding can only move the minute forward, so an `end_time` earlier than the
 * instant's own HH:MM (23:59:40 shown as 00:00) belongs to the following day.
 * Null when the response has no `ends_at` (older engines, fixtures).
 */
export function engineEndLocal(
  endTime: string | null | undefined,
  endsAt: string | null | undefined
): string | null {
  if (!endTime || !endsAt || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(endsAt)) return null;
  let day = endsAt.slice(0, 10);
  if (endTime < endsAt.slice(11, 16)) {
    const next = new Date(`${day}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    day = next.toISOString().slice(0, 10);
  }
  return `${day} ${endTime}`;
}

/**
 * True when the engine's end falls after `onDate`'s local midnight. For copy
 * that places the day itself ("upto 00:35 IST (next day)", "अगले दिन 00:35 तक")
 * instead of taking `engineEndClock`'s "00:35 (next day)" as one unit.
 */
export function engineEndsNextDay(
  end: { end_time?: string | null; ends_at?: string | null },
  onDate: string | null | undefined
): boolean {
  const local = onDate ? engineEndLocal(end.end_time, end.ends_at) : null;
  return local !== null && local.slice(0, 10) !== onDate;
}

/**
 * An engine end time for display on `onDate`'s page: "00:35 (next day)" when
 * it falls after that day's local midnight, the bare "21:05" otherwise or when
 * the date cannot be told.
 */
export function engineEndClock(
  end: { end_time?: string | null; ends_at?: string | null },
  onDate: string | null | undefined,
  locale: Locale
): string {
  const local = onDate ? engineEndLocal(end.end_time, end.ends_at) : null;
  return local && onDate ? clock(local, onDate, locale) : (end.end_time ?? "");
}
